import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { clientService, assignmentService, type AssignmentWithDetails } from '../../services'
import { useAuthStore } from '../../stores'
import { ClientForm, ClientAccessLink, type ClientFormData } from '../../components/client'
import { AssignmentList } from '../../components/assignment'
import { Button, LoadingScreen } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import type { Client, ClientUpdate } from '../../types/database.types'

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [client, setClient] = useState<Client | null>(null)
  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showEditForm, setShowEditForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (id && profile?.id) {
      loadClient()
      loadAssignments()
    }
  }, [id, profile?.id])

  const loadClient = async () => {
    if (!id) return
    setIsLoading(true)
    try {
      const data = await clientService.getClientById(id)
      setClient(data)
    } catch (error) {
      console.error('Failed to load client:', error)
      toast.error('Failed to load client')
    } finally {
      setIsLoading(false)
    }
  }

  const loadAssignments = async () => {
    if (!id || !profile?.id) return
    try {
      const data = await assignmentService.getAssignments(profile.id, {
        clientId: id,
      })
      setAssignments(data)
    } catch (error) {
      console.error('Failed to load assignments:', error)
    }
  }

  const handleUpdate = async (formData: ClientFormData) => {
    if (!id) return
    setIsSubmitting(true)
    try {
      const updated = await clientService.updateClient(id, formData.clientData as ClientUpdate)
      if (updated) {
        setClient(updated)
        toast.success('Client updated')
      }
      setShowEditForm(false)
    } catch (error) {
      toast.error('Failed to update client')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRegenerateToken = async () => {
    if (!id) return
    if (!confirm('Generate a new access link? The old link will stop working.')) {
      return
    }
    try {
      const updated = await clientService.regenerateAccessToken(id)
      if (updated) {
        setClient(updated)
        toast.success('New access link generated')
      }
    } catch (error) {
      toast.error('Failed to generate new link')
    }
  }

  const handleActivate = async () => {
    if (!id) return
    try {
      const updated = await clientService.activateClient(id)
      if (updated) {
        setClient(updated)
        toast.success('Client activated')
      }
    } catch (error) {
      toast.error('Failed to activate client')
    }
  }

  const handleArchive = async () => {
    if (!id || !client) return
    if (!confirm(`Archive ${client.full_name}? They will lose access to assigned programs.`)) {
      return
    }
    try {
      const updated = await clientService.archiveClient(id)
      if (updated) {
        setClient(updated)
        toast.success('Client archived')
      }
    } catch (error) {
      toast.error('Failed to archive client')
    }
  }

  const handleDelete = async () => {
    if (!id || !client) return
    if (!confirm(`Permanently delete ${client.full_name}? This action cannot be undone.`)) {
      return
    }
    try {
      await clientService.deleteClient(id)
      toast.success('Client deleted')
      navigate('/dashboard/clients')
    } catch (error) {
      toast.error('Failed to delete client')
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading client..." />
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900">Client Not Found</h1>
        <p className="mt-2 text-gray-600">
          The client you're looking for doesn't exist.
        </p>
        <Link to="/dashboard/clients" className="mt-4 inline-block">
          <Button variant="outline">Back to Clients</Button>
        </Link>
      </div>
    )
  }

  // Status config for icons
  const statusConfig: Record<string, { bg: string; iconColor: string; label: string }> = {
    invited: { bg: 'bg-yellow-100', iconColor: 'text-yellow-600', label: 'Invited - Awaiting first visit' },
    active: { bg: 'bg-green-100', iconColor: 'text-green-600', label: 'Active' },
    expired: { bg: 'bg-red-100', iconColor: 'text-red-600', label: 'Expired' },
    archived: { bg: 'bg-gray-100', iconColor: 'text-gray-500', label: 'Archived' },
  }

  // Status icon component
  const StatusIcon = ({ status }: { status: string }) => {
    const config = statusConfig[status]
    if (status === 'active') {
      return (
        <svg className={`w-4 h-4 ${config.iconColor}`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    if (status === 'invited') {
      return (
        <svg className={`w-4 h-4 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    if (status === 'expired') {
      return (
        <svg className={`w-4 h-4 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    // archived
    return (
      <svg className={`w-4 h-4 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    )
  }

  const config = statusConfig[client.status]

  // Tooltip wrapper component
  const Tooltip = ({ children, label }: { children: React.ReactNode; label: string }) => (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60]">
        {label}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </div>
    </div>
  )

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/clients"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {client.full_name}
              </h1>
              {/* Status indicators */}
              <div className="flex items-center gap-1.5">
                {/* Account icon if client has login credentials */}
                {client.user_id && (
                  <Tooltip label="Has login account">
                    <span className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center cursor-default">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </span>
                  </Tooltip>
                )}
                {/* Status icon */}
                <Tooltip label={config.label}>
                  <span className={`w-7 h-7 rounded-full ${config.bg} flex items-center justify-center cursor-default`}>
                    <StatusIcon status={client.status} />
                  </span>
                </Tooltip>
              </div>
            </div>
            <p className="text-gray-600">{client.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEditForm(true)}>
            Edit
          </Button>
          {/* Show Activate button only for invited clients WITHOUT accounts */}
          {client.status === 'invited' && !client.user_id && (
            <Button onClick={handleActivate}>Activate</Button>
          )}
          {/* Show Archive button for active clients */}
          {client.status === 'active' && (
            <Button variant="outline" onClick={() => handleArchive()}>
              Archive
            </Button>
          )}
        </div>
      </div>

      {/* Client info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Contact info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-500">Email</dt>
              <dd className="text-gray-900">{client.email}</dd>
            </div>
            {client.phone && (
              <div>
                <dt className="text-sm text-gray-500">Phone</dt>
                <dd className="text-gray-900">{client.phone}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-500">Added</dt>
              <dd className="text-gray-900">
                {new Date(client.created_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Access link */}
        {client.access_token && client.status !== 'archived' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Client Access</h3>
            <ClientAccessLink
              accessToken={client.access_token}
              onRegenerate={handleRegenerateToken}
            />
            {client.access_expires_at && (
              <p className="mt-3 text-sm text-gray-500">
                Access expires:{' '}
                {new Date(client.access_expires_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Notes */}
      {client.notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
          <p className="text-gray-600 whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}

      {/* Assignments */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Assigned Programs</h3>
          <Link to={`/dashboard/assignments?client=${client.id}`}>
            <Button size="sm">Assign Program</Button>
          </Link>
        </div>
        <AssignmentList
          assignments={assignments}
          emptyMessage="No programs assigned yet"
        />
      </div>

      {/* Danger zone */}
      <div className="border border-red-200 rounded-xl p-6">
        <h3 className="font-semibold text-red-600 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">
          Deleting a client will remove all their assignments and progress data.
          This action cannot be undone.
        </p>
        <Button variant="outline" onClick={handleDelete}>
          Delete Client
        </Button>
      </div>

      {/* Edit form modal */}
      {showEditForm && profile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowEditForm(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Edit Client
            </h2>
            <ClientForm
              client={client}
              providerId={profile.id}
              onSubmit={handleUpdate}
              onCancel={() => setShowEditForm(false)}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}
    </div>
  )
}
